import React, { useEffect, useRef } from 'react';
import { AppBar, Box, Container, Paper, Toolbar } from '@mui/material';
import {
	Sidebar,
	SIDEBAR_WIDTH,
	COLLAPSED_SIDEBAR_WIDTH,
} from 'src/globalComponents/Sidebar';
import { useGlobalState } from 'src/stateProviders/GlobalStateProvider';
import { Outlet } from 'react-router-dom';
import { Toaster } from 'sonner';
import { SidebarToggleButton } from './SidebarToggleButton';
import { useSidebarToggle } from '../hooks/useSidebarToggle';
import { useCanvasFullscreenToggle } from 'src/hooks/useCanvasFullscreenToggle';

export function DashboardLayout() {
	const globalState = useGlobalState();
	const { isCollapsed } = useSidebarToggle();
	const appBarRef = useRef<HTMLDivElement>(null);
	const { isCanvasFullscreen } = useCanvasFullscreenToggle();

	const currentSidebarWidth = isCollapsed
		? COLLAPSED_SIDEBAR_WIDTH
		: SIDEBAR_WIDTH;

	useEffect(() => {
		if (!appBarRef.current) return;

		const setAppBarHeightVar = () => {
			const height = appBarRef.current!.offsetHeight;
			document.documentElement.style.setProperty(
				'--app-bar-height',
				`${height}px`
			);
		};

		setAppBarHeightVar();

		const resizeObserver = new window.ResizeObserver(setAppBarHeightVar);
		resizeObserver.observe(appBarRef.current);

		window.addEventListener('resize', setAppBarHeightVar);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', setAppBarHeightVar);
		};
	}, [globalState.company?.profilePicture]);

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateRows: 'auto 1fr',
				gridTemplateColumns: `${currentSidebarWidth}px 1fr`,
				width: '100%',
				height: '100vh',
				overflowX: 'hidden',
				transition: 'grid-template-columns 0.3s ease-in-out',
			}}
		>
			<AppBar
				ref={appBarRef}
				position="sticky"
				sx={{
					zIndex: 2,
					bgcolor: 'secondary.main',
					py: 2,
					border: 0,
					gridColumn: '1 / -1',
					gridRow: '1',
				}}
			>
				<Toolbar
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						pl: isCollapsed ? 1 : 2, // Adjust padding based on sidebar state
						transition: 'padding 0.3s ease-in-out',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							width: isCollapsed ? '50px' : `calc(${SIDEBAR_WIDTH}px - 3rem)`,
							height: isCollapsed ? '50px' : 'auto',
							maxHeight: isCollapsed ? '50px' : '250px',
							transition: 'all 0.3s ease-in-out',
							overflow: 'hidden',
						}}
					>
						<Box
							component="img"
							alt="Company Logo"
							src={
								globalState.company?.profilePicture || '/deck_metrix_logo.svg'
							}
							sx={{
								width: isCollapsed ? '40px' : 'auto',
								height: isCollapsed ? '40px' : 'auto',
								maxWidth: isCollapsed
									? '40px'
									: `calc(${SIDEBAR_WIDTH}px - 3rem)`,
								maxHeight: isCollapsed ? '40px' : '250px',
								objectFit: isCollapsed ? 'contain' : 'contain',
								transition: 'all 0.3s ease-in-out',
								borderRadius: isCollapsed ? '8px' : '0px',
							}}
						/>
					</Box>
				</Toolbar>
			</AppBar>
			<Sidebar customSx={{ gridRow: '2', gridColumn: '1' }} />
			<Box
				sx={{
					gridRow: 2,
					gridColumn: 2,
					zIndex: 3,
					mt: 'calc(2 * var(--mui-spacing) - var(--app-bar-height))',
					mr: { xs: 1.5, sm: 1.5, md: 1, lg: 0.5, xl: 0.5 },
					height: 'calc(100% - 4 * var(--mui-spacing) + var(--app-bar-height))',
					overflow: 'hidden',
				}}
			>
				<Paper
					sx={{
						height: '100%',
						width: '100%',
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
						border: (theme) => `1px solid ${theme.palette.secondary['200']}`,
						boxShadow: 1,
						borderRadius: 1.5,
						backgroundColor: 'background.paper',
						position: 'relative',
					}}
				>
					{/* Sidebar Toggle Button */}
					{!isCanvasFullscreen && (
						<SidebarToggleButton
							sx={{
								position: 'absolute',
								top: 16,
								left: 16,
								zIndex: 4,
							}}
						/>
					)}

					<Container
						maxWidth="xl"
						sx={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							position: 'relative',
							overflow: 'auto',
							mt: 7,
							mb: { xs: 6, sm: 4 },
							pt: 2,
							pl: 4,
							pr: 4,
							'& [data-sonner-toaster="true"]': (theme) => ({
								'--gap': theme.spacing(2),
								'--offset-top': theme.spacing(4),
								'--offset-right': theme.spacing(4),
								'--offset-bottom': theme.spacing(4),
								'--offset-left': theme.spacing(4),
								[theme.breakpoints.down('sm')]: {
									'--offset-top': theme.spacing(2),
									'--offset-right': theme.spacing(2),
									'--offset-bottom': theme.spacing(2),
									'--offset-left': theme.spacing(2),
								},
							}),
						}}
					>
						<Toaster position="top-right" />
						<Outlet />
					</Container>
				</Paper>
			</Box>
		</Box>
	);
}
